import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import fetchService from '../../../services/fetchService';
import { PDF_ENDPOINTS } from '../../../endpoints';
import { entityService } from '../../../services/entityService';

export const pdfProcessCommand = {
  id: 'pdf_process',
  description: '📄 PROCESS PDF: Process and enrich a PDF document to make it readable by AI',
  logicalName: 'pdf_process',
  version: 'v1',
  appType: 'pdf',
  autonomyLevel: 'auto', // Auto-execute without approval for seamless UX
  visualTreatment: 'demonstrative',
  
  examples: {
    good: [
      {
        input: { entityId: '507f1f77bcf86cd799439011' },
        expectedOutput: {
          status: 'success',
          message: 'Successfully processed PDF',
          format: 'json',
          data: { type: 'pdf_processed', entityId: '507f1f77bcf86cd799439011' }
        } as StandardToolResult,
        description: 'Process an unprocessed PDF',
        context: 'When PDF is attached to context but not enriched'
      }
    ],
    bad: [
      {
        input: { entityId: '' },
        error: 'Missing entity ID',
        why: 'entityId parameter is required',
        fix: 'Provide valid PDF entity ID'
      }
    ]
  },
  
  performance: {
    expectedDuration: 30000, // 30 seconds average
    complexity: 'high',
    memoryIntensive: true
  },
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'PDF entity ID (_id)',
        required: true,
      },
      {
        name: 'llmId',
        type: 'string',
        description: 'PDF LLM ID (for reference)',
        required: false,
      },
      {
        name: 'fast_mode',
        type: 'boolean',
        description: 'Use fast processing mode (less detailed but faster)',
        required: false,
        defaultValue: false,
      }
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      const { entityId, fast_mode = false } = params;
      
      if (!entityId) {
        return createErrorResult('Missing entity ID', 'entityId parameter is required', 'validation');
      }

      // Emit progress for UX
      try {
        context.onProgress?.({ 
          type: 'streaming_update', 
          activity: 'Starting PDF processing...' 
        });
      } catch {}

      // Get entity to check if already processed
      let entity;
      try {
        entity = await entityService.fetchEntity(entityId);
      } catch (fetchError) {
        return createErrorResult(
          'PDF not found',
          `Could not find PDF entity with ID "${entityId}"`,
          'validation'
        );
      }

      const pdfName = entity?.name || entity?.skeleton?.fileName || 'PDF';
      const enrichmentStatus = entity?.skeleton?.enrichmentStatus;
      
      // Check if already processed
      if (enrichmentStatus && ['completed', 'completed_partial', 'completed_no_search'].includes(enrichmentStatus)) {
        return createSuccessResult(
          `PDF "${pdfName}" is already processed`,
          {
            type: 'pdf_already_processed',
            entityId,
            status: enrichmentStatus,
            message: `This PDF has already been enriched and is ready to read.`,
            metadata: {
              enrichmentStatus,
              executionTime: Date.now() - startTime
            }
          },
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              method: 'pdf_process_skipped'
            } as any
          }
        );
      }

      // Start enrichment process
      try {
        context.onProgress?.({ 
          type: 'streaming_update', 
          activity: `Processing PDF "${pdfName}"...` 
        });
      } catch {}

      let enrichData;
      try {
        enrichData = await fetchService(PDF_ENDPOINTS.enrich(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            file_id: entityId,
            fast_mode: fast_mode,
          }),
        });
      } catch (fetchError) {
        console.error('[pdf_process] PDF processing error:', fetchError);
        return createErrorResult(
          'Processing failed',
          `Failed to process PDF: ${fetchError instanceof Error ? fetchError.message : String(fetchError)}`,
          'execution'
        );
      }

      // fetchService returns parsed JSON, check for error status
      if (!enrichData || enrichData.error || enrichData.status === 'error') {
        console.error('[pdf_process] Backend error:', enrichData);
        return createErrorResult(
          'Processing failed to start',
          enrichData?.detail || enrichData?.error || 'Failed to start PDF processing',
          'execution'
        );
      }
      
      console.log('[pdf_process] Enrichment started:', {
        entityId,
        status: enrichData.status,
        message: enrichData.message
      });
      
      // If already processing, just poll for status
      if (enrichData.status === 'processing') {
        try {
          context.onProgress?.({ 
            type: 'streaming_update', 
            activity: 'PDF processing in progress, checking status...' 
          });
        } catch {}
      }

      // Poll for completion (max 60 seconds, check every 2 seconds)
      const maxAttempts = 30;
      let attempt = 0;
      let lastStatus = 'processing';
      
      while (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempt++;
        
        try {
          context.onProgress?.({ 
            type: 'streaming_update', 
            activity: `Processing PDF... (${Math.round((attempt / maxAttempts) * 100)}%)` 
          });
        } catch {}
        
        let statusData;
        try {
          statusData = await fetchService(
            `${PDF_ENDPOINTS.enrichStatus()}?fileId=${entityId}`,
            { method: 'GET' }
          );
        } catch (statusError) {
          console.warn('[pdf_process] Status check failed, continuing:', {
            attempt,
            error: statusError
          });
          continue; // Keep polling even if status check fails
        }
        
        lastStatus = statusData.status;
        
        console.log('[pdf_process] Poll attempt', attempt, ':', {
          status: lastStatus,
          hasData: !!statusData.data,
          hasError: !!statusData.error,
          hasWarning: !!statusData.warning
        });
        
        // Check for completion
        if (['completed', 'completed_partial', 'completed_no_search'].includes(lastStatus)) {
          // Success!
          return createSuccessResult(
            `Successfully processed PDF "${pdfName}"`,
            {
              type: 'pdf_processed',
              entityId,
              status: lastStatus,
              summary: statusData.data?.summary,
              totalPages: statusData.data?.totalPages,
              totalCharacters: statusData.data?.totalCharacters,
              searchIndexed: statusData.data?.searchIndexed,
              message: `PDF has been enriched and is now ready to read with doc_read_v2.`,
              metadata: {
                enrichmentStatus: lastStatus,
                processingTime: Date.now() - startTime,
                warning: statusData.warning
              }
            },
            {
              format: 'json',
              metadata: {
                executionTime: Date.now() - startTime,
                enrichmentStatus: lastStatus,
                method: 'pdf_process_completed'
              } as any
            }
          );
        }
        
        // Check for failure
        if (lastStatus === 'failed') {
          return createErrorResult(
            'PDF processing failed',
            statusData.error || 'PDF enrichment process failed',
            'execution',
            {
              metadata: {
                executionTime: Date.now() - startTime
              } as any
            }
          );
        }
      }
      
      // Timeout after max attempts
      return createErrorResult(
        'Processing timeout',
        `PDF processing is taking longer than expected. Current status: ${lastStatus}. The process may still complete in the background.`,
        'timeout',
        {
          metadata: {
            executionTime: Date.now() - startTime
          } as any
        }
      );
      
    } catch (error) {
      return createErrorResult(
        'Processing error',
        error instanceof Error ? error.message : 'Unknown error during PDF processing',
        'execution',
        {
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    }
  }
};

